import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Button from 'fe-design-base/molecules/Button';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { getAuthErrorMessage } from 'selectors/session';

import {
  GOOGLE_SIGN_IN_PATH,
  ROOT_PATH,
  SignInVariant,
} from 'features/authentication/SignIn/constants';
import HeaderLayout from 'features/authentication/SignIn/HeaderLayout';
import { mapAuthenticatePasswordErrorToMessage } from 'features/authentication/SignIn/helpers';
import SignUpSection from 'features/authentication/SignIn/SignUpSection';
import { SignInFormProps } from 'features/authentication/SignIn/types';

import { toI18n } from 'util/i18n';
import { useStytch } from 'util/stytch';

import EmailPasswordForm from './EmailPasswordForm';

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email(toI18n('fe_design_base.errors.field.email_invalid'))
    .required(toI18n('fe_design_base.errors.field.email_required')),
  password: Yup.string().required(
    toI18n('fe_design_base.errors.field.password_required')
  ),
});

interface FormValues {
  email: string;
  password: string;
}

const EmailSignIn = ({
  onChangeSignInType,
  onError,
  onSuccess,
}: SignInFormProps) => {
  const authErrorMessage = useSelector(getAuthErrorMessage);
  const [alertError, setAlertError] = useState(authErrorMessage || '');
  const stytchClient = useStytch();

  const handlePhoneSignInClick = useCallback(() => {
    onChangeSignInType(SignInVariant.PHONE);
  }, [onChangeSignInType]);

  const handleError = useCallback(
    // TODO: https://joinhomebase.atlassian.net/browse/FE-2199
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ({ error, setFieldError }) => {
      const { fieldError, alertError: alertErrorMsg } =
        mapAuthenticatePasswordErrorToMessage(error);

      if (fieldError) setFieldError('email', fieldError);
      if (alertErrorMsg) setAlertError(alertErrorMsg);
      onError(fieldError || alertErrorMsg);
    },
    [onError]
  );

  const handleSubmit = useCallback(
    (
      { email, password }: FormValues,
      { setSubmitting, setFieldError }: FormikHelpers<FormValues>
    ) => {
      if (alertError) setAlertError('');

      stytchClient
        .authenticatePassword(email, password)
        .then(() => {
          window.location.href = ROOT_PATH;
          onSuccess();
        })
        .catch(error => {
          handleError({ error, setFieldError });
          setSubmitting(false);
        });
    },
    [alertError, handleError, onSuccess, stytchClient]
  );

  return (
    <HeaderLayout
      title="authentication.sign_in"
      dataTestId="EmailSignInForm"
      alertError={alertError}
    >
      <Formik
        onSubmit={handleSubmit}
        initialValues={{
          email: '',
          password: '',
        }}
        validationSchema={validationSchema}
      >
        {({
          isSubmitting,
          isValid,
          dirty,
          handleSubmit: formikHandleSubmit,
          values,
          setFieldValue,
        }) => (
          <EmailPasswordForm
            formikHandleSubmit={formikHandleSubmit}
            isSubmitting={isSubmitting}
            dirty={dirty}
            isValid={isValid}
            values={values}
            setFieldValue={setFieldValue}
          />
        )}
      </Formik>
      <Box column gap={16} hcenter mb={32}>
        <Button
          variant="secondary"
          fullWidth
          startIcon="Phone"
          uxElement="phone sign in button"
          onClick={handlePhoneSignInClick}
        >
          {toI18n('authentication.phone_sign_in')}
        </Button>
        <Button
          variant="secondary"
          fullWidth
          startIcon="ColorGoogle"
          href={GOOGLE_SIGN_IN_PATH}
          uxElement="google sign in button"
        >
          {toI18n('authentication.google_sign_in')}
        </Button>
      </Box>
      <SignUpSection />
    </HeaderLayout>
  );
};

export default EmailSignIn;
